import React from 'react';
import Overview, { type OverviewRef } from '../Overview/Overview';
import {
  overviewFieldConfig,
  overviewFieldRenderPolicies,
  overviewFieldTypeFallbacks,
  omitEolExcludedOverviewFields
} from '../configs/overviewFieldConfig';
import { createOverviewConfigFromFormApi } from '../../../../utils/overviewMapping';
import { isEolMoment } from 'utils/constants';

export type SlideoutOverviewSectionProps = {
  overviewRef: React.Ref<OverviewRef>;
  overviewResetTrigger: number;
  adaptedOverviewData: Record<string, any>;
  card: Record<string, any>;
  formApiConfig: any;
  formApiLoading: boolean;
  onDataRefresh: (patchResponse?: unknown) => void;
  onNotificationRefresh: (patchResponse?: unknown) => void;
  onUnsavedChanges: (hasChanges: boolean) => void;
  onStrategicAlignmentError: (
    error: any,
    payload: Record<string, any>,
    intendedFieldValues?: Record<string, any>
  ) => void;
  onStrategicAlignmentUpdated: () => void;
};

/**
 * Overview config selection + Overview wiring (Part 2B-1).
 */
export function SlideoutOverviewSection({
  overviewRef,
  overviewResetTrigger,
  adaptedOverviewData,
  card,
  formApiConfig,
  formApiLoading,
  onDataRefresh,
  onNotificationRefresh,
  onUnsavedChanges,
  onStrategicAlignmentError,
  onStrategicAlignmentUpdated
}: SlideoutOverviewSectionProps) {
  const momentType = card.momentType || 'Product Release with Marketing';
  let activeOverviewConfig: any = { ...overviewFieldConfig };

  if (formApiConfig && !formApiLoading) {
    try {
      const dynamicConfig = createOverviewConfigFromFormApi(
        formApiConfig,
        momentType
      );
      if (Object.keys(dynamicConfig).length > 0) {
        activeOverviewConfig = dynamicConfig;
      }
    } catch (error) {
      // Keep using static config on error.
    }
  }

  if (!activeOverviewConfig.whyAreWeLaunching && !isEolMoment(momentType)) {
    activeOverviewConfig.whyAreWeLaunching =
      overviewFieldConfig.whyAreWeLaunching;
  }

  const staticAndAuxConfig: Record<string, unknown> = {
    ...overviewFieldConfig,
    ...overviewFieldTypeFallbacks
  };
  const overviewValues = adaptedOverviewData || {};
  for (const key of Object.keys(overviewValues)) {
    const renderPolicy = overviewFieldRenderPolicies[key];
    if (
      !renderPolicy?.requiresTemplateConfig &&
      !activeOverviewConfig[key] &&
      Object.prototype.hasOwnProperty.call(staticAndAuxConfig, key)
    ) {
      activeOverviewConfig[key] = (staticAndAuxConfig as any)[key];
    }
  }

  activeOverviewConfig = omitEolExcludedOverviewFields(
    activeOverviewConfig,
    momentType
  );

  const overviewUpdatedAt =
    card?.updated || card?.lastUpdated || card?._apiData?.updated;

  if (!activeOverviewConfig || Object.keys(activeOverviewConfig).length === 0) {
    return (
      <div data-testid="overview-loading" className="overview-loading">
        Loading overview configuration...
      </div>
    );
  }

  return (
    <Overview
      ref={overviewRef}
      key={overviewResetTrigger}
      overviewConfig={activeOverviewConfig}
      values={adaptedOverviewData}
      issueId={card?.issueId}
      headlineUpdatedAt={card?.overview?.headlineUpdatedAt}
      updated={overviewUpdatedAt}
      onDataRefresh={onDataRefresh}
      onNotificationRefresh={onNotificationRefresh}
      onUnsavedChanges={onUnsavedChanges}
      onStrategicAlignmentError={onStrategicAlignmentError}
      onStrategicAlignmentUpdated={onStrategicAlignmentUpdated}
    />
  );
}
